/**
 * Zoom + pan for the maximised screen share (board m5gc #23). The video
 * element fills the viewport box (object-fit: contain) and gets a CSS
 * `translate(tx, ty) scale(s)` with the origin at its centre; these helpers
 * keep that transform inside the box, so the frame can never be pushed out
 * of view. Pure, so the arithmetic is tested without a DOM.
 */

export interface ZoomState {
  scale: number
  /** Translation in CSS pixels, applied before the scale (origin: centre). */
  tx: number
  ty: number
}

export const ZOOM_MIN = 1
export const ZOOM_MAX = 5
export const ZOOM_STEP = 1.25

export const FIT: ZoomState = { scale: 1, tx: 0, ty: 0 }

/** Clamp the pan so the scaled frame always covers the box (no gap on any side). */
export function clampPan(state: ZoomState, box: { width: number; height: number }): ZoomState {
  const scale = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, state.scale))
  const maxX = ((scale - 1) * box.width) / 2
  const maxY = ((scale - 1) * box.height) / 2
  // `+ 0` folds a -0 (from clamping a negative pan at fit) into 0.
  return {
    scale,
    tx: Math.min(maxX, Math.max(-maxX, state.tx)) + 0,
    ty: Math.min(maxY, Math.max(-maxY, state.ty)) + 0,
  }
}

/**
 * Zoom by `factor` around a point (`cx`, `cy` in box pixels, from the box's
 * top-left), so the content under the cursor / between the fingers stays put.
 */
export function zoomAt(
  state: ZoomState,
  factor: number,
  point: { cx: number; cy: number },
  box: { width: number; height: number }
): ZoomState {
  const nextScale = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, state.scale * factor))
  const ratio = nextScale / state.scale
  // Cursor position relative to the box centre; the content point under it
  // is at (p - t) / s in unscaled coordinates, and must map to p again.
  const px = point.cx - box.width / 2
  const py = point.cy - box.height / 2
  return clampPan(
    {
      scale: nextScale,
      tx: px - (px - state.tx) * ratio,
      ty: py - (py - state.ty) * ratio,
    },
    box
  )
}

export function panBy(
  state: ZoomState,
  delta: { dx: number; dy: number },
  box: { width: number; height: number }
): ZoomState {
  return clampPan({ ...state, tx: state.tx + delta.dx, ty: state.ty + delta.dy }, box)
}

export function isFit(state: ZoomState): boolean {
  return state.scale <= ZOOM_MIN
}
